import { Component, Injector, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import { HotToastService } from '@ngneat/hot-toast';
import { CookieService } from 'ngx-cookie-service';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { B2bMeetingsService } from '@conciliome/b2b-api';
import { B2bFormService, CtaSeoQuery, LivreBlancQuery } from '@b2b-app/b2b-core';
import { CtaSeo } from '@conciliome/concilio-model';
import { ConcilioComponent } from '@conciliome/concilio-core';
import { of } from 'rxjs';

@Component({
  selector: 'b2b-ui-livre-blanc-details',
  templateUrl: './b2b-livre-blanc-details.component.html',
  styleUrls: ['./b2b-livre-blanc-details.component.scss'],
})
export class B2bLivreBlancDetailsComponent extends ConcilioComponent implements OnInit {
  pageSlug: string;
  livresblanc$ = this.lvQuery.livresblanc$;
  livreBlanc: any;
  ctaSeo: CtaSeo;
  form = new FormControl();

  constructor(
    injector: Injector,
    private meta: Meta,
    private titleService: Title,
    private toastService: HotToastService,
    private lvQuery: LivreBlancQuery,
    private ctaSeoQuery: CtaSeoQuery,
    private hubspotContactsApi: B2bMeetingsService,
    private b2bFormService: B2bFormService,
    private cookieService: CookieService
  ) {
    super(injector);

    this.addSubscription(
      this.route.params
        .pipe(
          tap((params) => {
            this.pageSlug = params['id'];
          }),
          switchMap((params) => this.lvQuery.livresblanc$),
          withLatestFrom(this.ctaSeoQuery.allCtas$)
        )
        .subscribe(([livres, ctas]) => {
          this.livreBlanc = livres?.find((livre) => livre.slug === this.pageSlug);
          this.ctaSeo = (ctas as any).find((cta) => cta.slug === 'cta-seo');
          this.updateTags();
        })
    );
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.addSubscription(
      this.form.valueChanges
        .pipe(
          map((result) => {
            const pageUri = this.getPageUri();

            return {
              company: result.properties.company,
              email: result.properties.email,
              firstname: result.properties.firstname,
              lastname: result.properties.lastname,
              phone: result.properties.phone,
              address: '', //adresse postale
              jobtitle: '', // poste
              company_size: '', // taille totale de la boite (string)
              company_size_scope: '',
              pageUri: pageUri,
              pageName: this.titleService.getTitle(),
              hutk: this.cookieService.get('hubspotutk'),
            };
          }),
          tap((contact) => {
            this.b2bFormService.createContact(contact);
          }),
          switchMap((value) =>
            this.hubspotContactsApi.createContact(value).pipe(
              tap(() => {
                this.toastService.loading('Demande en cours...', { id: 'download' });
              }),
              // this.toastService.observe({
              //   loading: 'Téléchargement du document en cours...',
              //   success: `C'est parti! Vous allez recevoir un email avec le lien de téléchargement du document.`,
              //   error: `
              //   <b>Erreur de Téléchargement</b>
              //   <p>Une erreur est survenue lors du téléchargement du document!</p>
              //   <p>Veuillez réessayer.</p>
              // `,
              // }),
              catchError((error) => of(error))
            )
          ),
          catchError((error) => of(error))
        )
        .subscribe(
          (data) => {
            this.toastService.close('download');
            this.routerService.navigate([`/livre-blanc/${this.pageSlug}/confirmation`]);
          },
          () => {
            this.toastService.error(
              `Une erreur est survenue lors de l'enregistrement du contact. Veuillez verifiez vos informations. Sinon, contactez un administrateur.`,
              { autoClose: false, dismissible: true }
            );
          }
        )
    );
  }

  updateTags() {
    if (this.livreBlanc) {
      this.titleService.setTitle(this.livreBlanc.titre);
      this.meta.updateTag({ name: 'title', content: this.livreBlanc.meta_titre });
      this.meta.updateTag({ name: 'description', content: this.livreBlanc.meta_description });
      this.meta.updateTag({ name: 'og:title', content: this.livreBlanc.meta_titre });
      this.meta.updateTag({ name: 'og:description', content: this.livreBlanc.meta_description });
    }
  }

  private getPageUri(): string {
    const pageUri = `${location.origin}${location.pathname}`;
    let updatedPageUri = pageUri;

    if (this.cookieService.get('utm_source')) {
      updatedPageUri =
        updatedPageUri === pageUri
          ? `${updatedPageUri}?utm_source=${encodeURI(this.cookieService.get('utm_source'))}`
          : `${updatedPageUri}&utm_source=${encodeURI(this.cookieService.get('utm_source'))}`;
    }

    if (this.cookieService.get('utm_campaign')) {
      updatedPageUri =
        updatedPageUri === pageUri
          ? `${updatedPageUri}?utm_campaign=${encodeURI(this.cookieService.get('utm_campaign'))}`
          : `${updatedPageUri}&utm_campaign=${encodeURI(this.cookieService.get('utm_campaign'))}`;
    }

    if (this.cookieService.get('utm_medium')) {
      updatedPageUri =
        updatedPageUri === pageUri
          ? `${updatedPageUri}?utm_medium=${encodeURI(this.cookieService.get('utm_medium'))}`
          : `${updatedPageUri}&utm_medium=${encodeURI(this.cookieService.get('utm_medium'))}`;
    }

    if (this.cookieService.get('utm_term')) {
      updatedPageUri =
        updatedPageUri === pageUri
          ? `${updatedPageUri}?utm_term=${encodeURI(this.cookieService.get('utm_term'))}`
          : `${updatedPageUri}&utm_term=${encodeURI(this.cookieService.get('utm_term'))}`;
    }

    if (this.cookieService.get('utm_content')) {
      updatedPageUri =
        updatedPageUri === pageUri
          ? `${updatedPageUri}?utm_content=${encodeURI(this.cookieService.get('utm_content'))}`
          : `${updatedPageUri}&utm_content=${encodeURI(this.cookieService.get('utm_content'))}`;
    }

    return updatedPageUri;
  }
}
